import { useFile } from "#src/hooks/useFile";
import {
  ButtonToggleGroup,
  FileUploadInput,
  TextAreaInput,
  useFormContext,
} from "@validereinc/common-components";
import classNames from "classnames/bind";
import React, { useCallback, useMemo, useRef } from "react";
import styles from "./WorkerInputFileField.module.scss";
import {
  formatWorkerFileReferenceJson,
  getWorkerFileInputModeFieldName,
  getWorkerFileInputReferenceFieldName,
  validateWorkerFileReferenceJson,
  validateWorkerMultiFileCount,
  WorkerFileInputMode,
  type WorkerFileInputModeType,
} from "./WorkerInputFileField.helpers";

const cx = classNames.bind(styles);

const WORKER_FILE_INPUT_MODE_TOGGLE_ITEMS = [
  { label: "Upload", dataKey: WorkerFileInputMode.UPLOAD },
  { label: "Reference", dataKey: WorkerFileInputMode.REFERENCE },
];

const SINGLE_FILE_REFERENCE_PLACEHOLDER = `{
  "name": "report.csv",
  "ref": "00000000-0000-0000-0000-000000000000"
}`;

const MULTI_FILE_REFERENCE_PLACEHOLDER = `[
  {
    "name": "report.csv",
    "ref": "00000000-0000-0000-0000-000000000000"
  }
]`;

export type WorkerInputFileFieldProps = {
  /** React Hook Form field name for the uploaded-file value. */
  name: string;
  /** Input-schema property key; used for sibling mode/reference field names. */
  schemaKey: string;
  label: string;
  isRequired: boolean;
  description?: string;
  allowMultiple: boolean;
};

/**
 * FILE / MULTI_FILE picker for worker input schemas. Upload mode sends files
 * through the Data Platform file service; reference mode accepts JSON with
 * `{ name, ref }` where `ref` is a UUID.
 */
export const WorkerInputFileField = ({
  name,
  schemaKey,
  label,
  isRequired,
  description,
  allowMultiple,
}: WorkerInputFileFieldProps) => {
  const { handleFileDownload, handleFileUpload } = useFile();
  const { setValue, getValues, watch } = useFormContext();

  const modeFieldName = getWorkerFileInputModeFieldName(schemaKey);
  const referenceFieldName = getWorkerFileInputReferenceFieldName(schemaKey);
  const mode =
    (watch(modeFieldName) as WorkerFileInputModeType | undefined) ??
    WorkerFileInputMode.UPLOAD;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const isUploadMode = mode === WorkerFileInputMode.UPLOAD;
  const isReferenceMode = mode === WorkerFileInputMode.REFERENCE;

  const handleModeChange = useCallback(
    (nextModeKey: string) => {
      const nextMode = nextModeKey as WorkerFileInputModeType;

      if (nextMode === WorkerFileInputMode.REFERENCE) {
        setValue(
          referenceFieldName,
          formatWorkerFileReferenceJson(getValues(name), allowMultiple)
        );
      }

      setValue(modeFieldName, nextMode);
    },
    [
      allowMultiple,
      getValues,
      modeFieldName,
      name,
      referenceFieldName,
      setValue,
    ]
  );

  const validateReferenceJson = useCallback(
    (value: string) => {
      if (modeRef.current !== WorkerFileInputMode.REFERENCE) {
        return true;
      }

      return validateWorkerFileReferenceJson(value, {
        allowMultiple,
        isRequired,
        label,
      });
    },
    [allowMultiple, isRequired, label]
  );

  const referenceFieldRules = useMemo(
    () => ({
      validate: {
        fileReferenceJson: validateReferenceJson,
      },
    }),
    [validateReferenceJson]
  );

  const validateUploadFileCount = useCallback(
    (value: unknown) => {
      if (modeRef.current !== WorkerFileInputMode.UPLOAD || !allowMultiple) {
        return true;
      }

      return validateWorkerMultiFileCount(value);
    },
    [allowMultiple]
  );

  const uploadFieldRules = useMemo(
    () => ({
      validate: {
        maxFiles: validateUploadFileCount,
      },
    }),
    [validateUploadFileCount]
  );

  return (
    <div className={cx("worker-input-file-field")}>
      <ButtonToggleGroup
        name={modeFieldName}
        className={cx("worker-input-file-field__mode")}
        items={WORKER_FILE_INPUT_MODE_TOGGLE_ITEMS}
        activeKey={mode}
        onChange={handleModeChange}
      />
      <div
        className={cx({
          "worker-input-file-field__panel--hidden": !isUploadMode,
        })}
      >
        <FileUploadInput
          id={name}
          name={name}
          label={label}
          isRequired={isRequired && isUploadMode}
          description={description}
          allowMultiple={allowMultiple}
          isFluid
          rules={uploadFieldRules}
          uploadFile={handleFileUpload}
          downloadFile={handleFileDownload}
        />
      </div>
      <div
        className={cx({
          "worker-input-file-field__panel--hidden": !isReferenceMode,
        })}
      >
        <TextAreaInput
          name={referenceFieldName}
          label={label}
          isRequired={isRequired && isReferenceMode}
          description={description}
          placeholder={
            allowMultiple
              ? MULTI_FILE_REFERENCE_PLACEHOLDER
              : SINGLE_FILE_REFERENCE_PLACEHOLDER
          }
          rows={8}
          isFluid
          rules={referenceFieldRules}
        />
      </div>
    </div>
  );
};
